import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col, Form, Button, Spinner, Dropdown, ProgressBar } from 'react-bootstrap';
import axios from 'axios';
import ReactPlayer from 'react-player';
import { FaPlay, FaPause, FaDownload, FaMusic, FaChevronDown, FaChevronUp, FaSearch, FaPlus, FaExchangeAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';

// API 固定同源 /api（构建时不再依赖 REACT_APP_API_BASE，避免路径污染）
const API_BASE = '/api';

// 实际可从本站服务器稳定取数的音源（HK 服务器网络限制：腾讯/酷我/百度/虾米/海外源不可达，故仅保留这 3 个）
const WORKING_SOURCES = [
  { key: 'netease', name: '网易云', color: '#E60026' },
  { key: 'kugou', name: '酷狗', color: '#0099FF' },
  { key: 'migu', name: '咪咕', color: '#FF6A00' },
];
const ALL_SOURCE = { key: 'all', name: '全部平台', color: '#16a34a' };
const AGG_SOURCES = WORKING_SOURCES.map(s => s.key);

// 归一化歌名+歌手，用于跨平台去重
const norm = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/[\s~!@#$%^&*()_+=\-\[\]\\{}|;:'",.<>/?？！，。、；：''""（）【】《》…—·~]/g, '');

const MusicSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [mode, setMode] = useState('all'); // 'all' | 'netease' | 'kugou' | 'migu'
  const [quality, setQuality] = useState('999');
  const [loading, setLoading] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [playerUrl, setPlayerUrl] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  const playerRef = useRef(null);
  const [coverCache, setCoverCache] = useState({});
  const [playlist, setPlaylist] = useState([]);
  const [lyricData, setLyricData] = useState({
    rawLyric: '',
    tLyric: '',
    parsedLyric: []
  });
  const [currentLyricIndex, setCurrentLyricIndex] = useState(-1);
  const [lyricExpanded, setLyricExpanded] = useState(false);
  const lyricsContainerRef = useRef(null);

  const qualities = ['128', '192', '320', '740', '999'];

  // 首页热门歌曲实时拉取的歌单：网易云飙升榜（id 19723756，变化快，最能体现实时）
  const NETEASE_HOT_PLAYLIST = '19723756';
  const HOT_PLAYLIST_NAME = '网易云飙升榜';
  // trendingSource: 'loading'(拉取中) | 'live'(实时拉取成功) | 'fallback'(接口失败用兜底)
  const [trendingSongs, setTrendingSongs] = useState([
    { name: '全息恋人', artist: '栾一鸣Floscar' },
    { name: 'One Of The Girls', artist: 'The Weeknd/JENNIE/Lily-Rose Depp' },
    { name: '偷心', artist: 'SASIOVERLXRD/连麻Swimming' },
    { name: 'Know My Name', artist: 'True Damage/英雄联盟/Sofía Reyes/VIC MENSA/Coco Jones/田小娟/i-dle' },
    { name: '学功夫练武术', artist: '概念黁' },
    { name: '遗憾总是贯穿人生始终', artist: '边缘/粘了咕叽' },
    { name: '泡沫', artist: 'G.E.M.邓紫棋' },
    { name: '于是', artist: 'G.E.M.邓紫棋' },
    { name: '趁早', artist: '张惠妹' },
    { name: '别来无恙', artist: '任素汐' },
    { name: 'FF', artist: 'Troye Sivan' },
    { name: 'Canada Dry', artist: 'Troye Sivan' }
  ]);
  const [trendingSource, setTrendingSource] = useState('loading');

  // 打开首页即拉取网易云飙升榜，替换热门歌曲（失败则保留兜底并标记）
  useEffect(() => {
    let alive = true;
    setTrendingSource('loading');
    axios.get(`${API_BASE}?types=playlist&source=netease&id=${NETEASE_HOT_PLAYLIST}`)
      .then(res => {
        let data = res.data;
        if (typeof data === 'string') {
          try { data = JSON.parse(data); } catch (e) { if (alive) setTrendingSource('fallback'); return; }
        }
        const list = (Array.isArray(data) ? data : []).slice(0, 12).map(s => ({
          name: s.name,
          artist: Array.isArray(s.artist) ? s.artist.join('/') : (s.artist || ''),
          pic_id: s.pic_id || ''
        })).filter(s => s.name);
        if (alive && list.length) {
          setTrendingSongs(list);
          setTrendingSource('live');
          list.forEach(s => { if (s.pic_id) fetchCover('netease', s.pic_id); });
        }
        else if (alive) setTrendingSource('fallback');
      })
      .catch(() => { if (alive) setTrendingSource('fallback'); });
    return () => { alive = false; };
  }, []);

  const parseLyric = (text) => {
    const lines = (text || '').split('\n');
    const pattern = /\[(\d+):(\d+\.\d+)\]/;
    return lines.map(line => {
      const match = line.match(pattern);
      if (match) {
        const minutes = parseFloat(match[1]);
        const seconds = parseFloat(match[2]);
        return {
          time: minutes * 60 + seconds,
          text: line.replace(match[0], '').trim()
        };
      }
      return null;
    }).filter(Boolean);
  };

  // 单源搜索
  const fetchSource = async (src, keyword, count) => {
    const res = await axios.get(`${API_BASE}`, {
      params: { types: 'search', source: src, name: keyword, count, pages: 1 }
    });
    return (res.data || []).map(t => ({ ...t, source: src }));
  };

  // 多源结果合并去重
  const mergeResults = (lists) => {
    const map = new Map();
    let order = 0;
    lists.forEach(arr => (arr || []).forEach(t => {
      const artist = Array.isArray(t.artist) ? t.artist.join('/') : (t.artist || '');
      const k = norm(t.name) + '|' + norm(artist);
      let cur = map.get(k);
      if (!cur) {
        cur = { key: k, name: t.name, artist, album: t.album || '', sources: {}, order: order++ };
      }
      cur.sources[t.source] = {
        id: t.id, pic_id: t.pic_id, lyric_id: t.lyric_id || t.id, source: t.source, album: t.album
      };
      map.set(k, cur);
    }));
    return [...map.values()]
      .sort((a, b) => a.order - b.order)
      .map(it => ({ ...it, activeSource: AGG_SOURCES.find(s => it.sources[s]) || null }));
  };

  const handleSearch = async (e, directQuery = '') => {
    if (e && e.preventDefault) e.preventDefault();
    const keyword = (directQuery || query || '').trim();
    if (!keyword) return;

    setLoading(true);
    try {
      let merged;
      if (mode === 'all') {
        const lists = await Promise.allSettled(
          AGG_SOURCES.map(src => fetchSource(src, keyword, 20))
        );
        merged = mergeResults(lists.filter(r => r.status === 'fulfilled').map(r => r.value));
      } else {
        const list = await fetchSource(mode, keyword, 30);
        merged = mergeResults([list]).map(it => ({ ...it, activeSource: mode }));
      }

      // 预取各结果当前激活源的封面
      merged.forEach(it => {
        const t = it.sources[it.activeSource];
        if (t) fetchCover(it.activeSource, t.pic_id);
      });

      setResults(merged);
      if (merged.length === 0) {
        toast.info('未找到结果，试试换关键词或更短的名字', { icon: '🔍', className: 'custom-toast' });
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('搜索失败，请稍后重试', { icon: '❌', className: 'custom-toast error-toast' });
    }
    setLoading(false);
  };

  const fetchCover = async (source, picId, size = 300) => {
    const cacheKey = `${source}-${picId}-${size}`;
    if (coverCache[cacheKey]) return coverCache[cacheKey];
    try {
      const response = await axios.get(`${API_BASE}`, {
        params: { types: 'pic', source: source, id: picId, size }
      });
      const url = response.data.url.replace(/\\/g, '');
      setCoverCache(prev => ({ ...prev, [cacheKey]: url }));
      return url;
    } catch (error) {
      console.error('封面获取失败:', error);
      return 'default_cover.png';
    }
  };

  // 切换某首歌的播放音源
  const switchSource = (item, src) => {
    if (src === item.activeSource) return;
    setResults(prev => prev.map(it => it.key === item.key ? { ...it, activeSource: src } : it));
    const t = item.sources[src];
    if (t && !coverCache[`${src}-${t.pic_id}-300`]) fetchCover(src, t.pic_id);
  };

  const resolveTrack = (item) => {
    const t = item.sources[item.activeSource];
    if (!t) return null;
    return { ...t, name: item.name, artist: item.artist, album: item.album };
  };

  const handlePlay = async (item) => {
    const track = resolveTrack(item);
    if (!track) return;
    if (currentTrack?.id === track.id && currentTrack?.source === track.source) {
      setIsPlaying(!isPlaying);
      return;
    }
    try {
      const [urlResponse, lyricResponse] = await Promise.all([
        axios.get(API_BASE, { params: { types: 'url', source: track.source, id: track.id, br: quality } }),
        axios.get(API_BASE, { params: { types: 'lyric', source: track.source, id: track.lyric_id } })
      ]);

      const rawLyric = lyricResponse.data.lyric || '';
      const tLyric = lyricResponse.data.tlyric || '';
      setLyricData({ rawLyric, tLyric, parsedLyric: parseLyric(rawLyric) });

      setPlayerUrl('');
      setIsPlaying(false);

      const response = await axios.get(`${API_BASE}`, {
        params: { types: 'url', source: track.source, id: track.id, br: quality }
      });
      const url = response.data?.url?.replace(/\\/g, '');
      if (!url) throw new Error('无效的音频链接');

      setCurrentTrack(track);
      setShowPlayer(true);
      setPlayerUrl(url);
      setIsPlaying(true);
    } catch (error) {
      console.error('Play error:', error);
      setIsPlaying(false);
      setPlayerUrl('');
      toast.warning('当前音源暂不可用，点歌曲上的其它平台图标换源试试', {
        icon: '⚠️', className: 'custom-toast warning-toast'
      });
    }
  };

  // 关闭试听：停止播放并隐藏播放器
  const closePlayer = () => {
    setIsPlaying(false);
    setLyricExpanded(false);
    setShowPlayer(false);
    setPlayerUrl('');
    setCurrentTrack(null);
  };

  const useThrottle = (callback, delay) => {
    const lastCall = useRef(0);
    return useCallback((...args) => {
      const now = new Date().getTime();
      if (now - lastCall.current >= delay) {
        lastCall.current = now;
        callback(...args);
      }
    }, [callback, delay]);
  };

  const handleProgress = useThrottle((state) => {
    const currentTime = state.playedSeconds;
    const lyrics = lyricData.parsedLyric;
    let newIndex = -1;
    for (let i = lyrics.length - 1; i >= 0; i--) {
      if (currentTime >= lyrics[i].time) { newIndex = i; break; }
    }
    if (newIndex !== currentLyricIndex) setCurrentLyricIndex(newIndex);
  }, 500);

  const [downloadModal, setDownloadModal] = useState({ show: false, track: null, progress: 0 });

  const handleDownload = async (item) => {
    const track = resolveTrack(item);
    if (!track) return;
    setDownloadModal({ show: true, track, progress: 0 });
    try {
      // 仅向服务器索取“真实直链”（几 KB 的 JSON，不传音频，不消耗服务器流量）
      const params = new URLSearchParams({
        types: 'url', source: track.source, id: track.id, br: quality
      });
      const info = await fetch(`${API_BASE}?${params.toString()}`).then(r => r.json());
      let directUrl = String(info.url || '').replace(/\\/g, '');
      if (!directUrl) throw new Error('该音源未返回可下载地址，请切换音源重试');

      // 页面走 https 时，把 http 直链升级为 https，避免浏览器拦截混合内容
      if (window.location.protocol === 'https:' && directUrl.startsWith('http://')) {
        directUrl = 'https://' + directUrl.slice(7);
      }

      // 浏览器【直连音乐平台 CDN】拉取音频 —— 完全绕开我们服务器，零服务器流量
      const response = await fetch(directUrl, { mode: 'cors' });
      if (!response.ok) throw new Error(`音源 CDN 响应异常: ${response.status}`);

      const total = parseInt(response.headers.get('Content-Length')) || 0;
      const reader = response.body.getReader();
      const chunks = [];
      let received = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        if (total > 0) {
          setDownloadModal(prev => ({ ...prev, progress: Math.round(received / total * 100) }));
        }
      }

      const blob = new Blob(chunks, { type: 'audio/mpeg' });
      const blobUrl = URL.createObjectURL(blob);
      const ext = getFileExtension(directUrl) === 'audio' ? 'mp3' : getFileExtension(directUrl);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${track.name || '音乐'} - ${track.artist || '未知歌手'}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);

      toast.success('下载完成', { icon: '✅', className: 'custom-toast success-toast' });
    } catch (error) {
      console.error('Download error:', error);
      toast.error('下载失败：' + (error.message || '请稍后重试'), {
        icon: '❌', className: 'custom-toast error-toast'
      });
    } finally {
      setTimeout(() => setDownloadModal({ show: false, track: null, progress: 0 }), 800);
    }
  };

  const addToPlaylist = (item) => {
    const track = resolveTrack(item);
    if (!track) return;
    setPlaylist(prev => {
      if (prev.some(t => t.id === track.id && t.source === track.source)) {
        toast.info('已在播放列表中', { icon: 'ℹ️', className: 'custom-toast' });
        return prev;
      }
      toast.success('已添加到播放列表', { icon: '➕', className: 'custom-toast success-toast' });
      return [...prev, track];
    });
  };

  const getFileExtension = (url) => {
    try {
      const cleanUrl = url.replace(/\\/g, '');
      const fileName = new URL(cleanUrl).pathname.split('/').pop().split(/[#?]/)[0];
      const extensionMatch = fileName.match(/\.([a-z0-9]+)$/i);
      return extensionMatch ? extensionMatch[1] : 'audio';
    } catch {
      return 'audio';
    }
  };

  // ===== SEO：进入带 ?q= 自动搜索 + 动态 title/description + JSON-LD =====
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let q = params.get('q');
    // 伪静态路径 /s/关键词 也自动搜索（与 SEO 快照共用同一 URL，用户点开体验一致）
    if (!q && window.location.pathname.startsWith('/s/')) {
      try { q = decodeURIComponent(window.location.pathname.slice(3)); } catch (e) { q = window.location.pathname.slice(3); }
    }
    if (q) {
      setQuery(q);
      handleSearch(null, q);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const HOME_TITLE = '免费音乐搜索 - 免费在线听歌、MP3下载｜网易云/酷狗/咪咕聚合';
    const base = '免费音乐搜索';
    document.title = query ? `${query} - ${base}` : HOME_TITLE;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', query
        ? `搜索“${query}”，免费在线听歌、MP3下载，聚合网易云、酷狗、咪咕多平台音乐，歌词同步，无需注册。`
        : '免费音乐搜索，聚合网易云、酷狗、咪咕多平台曲库。支持免费在线听歌、MP3下载、音乐下载，歌词同步，无需注册登录，手机电脑均可使用。');
    }
    injectJSONLD(query, results);
    // eslint-disable-next-line
  }, [query, results]);

  const injectJSONLD = (q, list) => {
    let el = document.getElementById('jsonld-music');
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = 'jsonld-music';
      document.head.appendChild(el);
    }
    if (!list || list.length === 0) {
      el.textContent = q
        ? JSON.stringify({ '@context': 'https://schema.org', '@type': 'SearchResultsPage', name: `${q} 音乐搜索` })
        : JSON.stringify({ '@context': 'https://schema.org', '@type': 'WebSite', name: '免费音乐搜索', alternateName: '免费在线听歌 MP3下载', url: window.location.origin });
      return;
    }
    const items = list.slice(0, 20).map((it, i) => ({
      '@type': 'MusicRecording',
      '@id': `${window.location.origin}/?q=${encodeURIComponent(it.name)}`,
      name: it.name,
      byArtist: { '@type': 'MusicGroup', name: it.artist },
      ...(it.album ? { inAlbum: { '@type': 'MusicAlbum', name: it.album } } : {}),
      position: i + 1
    }));
    el.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: q ? `${q} 的搜索结果` : '免费音乐搜索',
      itemListElement: items
    });
  };

  useEffect(() => {
    if (lyricExpanded && currentLyricIndex >= 0 && lyricsContainerRef.current) {
      const activeLines = lyricsContainerRef.current.getElementsByClassName('active');
      if (activeLines.length > 0) {
        activeLines[0].scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    }
  }, [currentLyricIndex, lyricExpanded]);

  return (
    <Container className="my-4 app-container" style={{ paddingBottom: lyricExpanded ? '320px' : '120px', transition: 'padding 0.3s ease' }}>
      <div className="hero-section text-center">
        <h1 className="hero-title">免费音乐搜索</h1>
        <p className="hero-subtitle">聚合 网易云 / 酷狗 / 咪咕 多平台曲库，免费在线听歌、MP3下载，一次搜索全网结果，支持歌词同步与高品质下载</p>

        <Form onSubmit={handleSearch} className="search-form mx-auto">
          <div className="search-bar">
            <div className="search-input-wrap">
              <FaSearch className="search-icon" />
              <input
                type="search"
                className="search-input"
                placeholder="歌曲名、歌手或专辑..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Dropdown className="quality-dropdown">
              <Dropdown.Toggle variant="light" className="pick-toggle">音质: {quality}k</Dropdown.Toggle>
              <Dropdown.Menu>
                {qualities.map(q => (
                  <Dropdown.Item key={q} onClick={() => setQuality(q)} active={q === quality}>{q}k</Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <Button type="submit" className="search-btn"><FaSearch /> 搜索</Button>
          </div>
        </Form>

        {/* 音源切换：圆点 radio 风格 */}
        <div className="source-tabs">
          {[ALL_SOURCE, ...WORKING_SOURCES].map(s => (
            <button key={s.key} type="button" className={`source-tab ${mode === s.key ? 'active' : ''}`} onClick={() => setMode(s.key)}>
              <span className="radio-dot" style={{ background: mode === s.key ? s.color : 'transparent', borderColor: s.color }}></span>
              <span>{s.name}</span>
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="text-center my-4"><Spinner animation="border" /></div>
      )}

      {!loading && results.length === 0 && (
        <div className="trending-section">
          <div className="section-head text-center">
            <div className="section-badge">TRENDING</div>
            <h3 className="section-title">🔥 热门歌曲</h3>
          </div>
          <Row className="g-3">
            {trendingSongs.map((song, idx) => (
              <Col key={idx} xs={12} sm={6} lg={4}>
                <div className="trending-card" onClick={() => { setQuery(song.name); handleSearch(null, song.name); }}>
                  <img
                    className="trending-cover"
                    src={song.pic_id ? (coverCache[`netease-${song.pic_id}-300`] || 'default_cover.png') : 'default_cover.png'}
                    alt={song.name}
                  />
                  <span className="trending-rank">{idx + 1}</span>
                  <div className="trending-info">
                    <div className="trending-name">{song.name}</div>
                    <div className="trending-artist">{song.artist}</div>
                  </div>
                  <FaSearch className="trending-go" />
                </div>
              </Col>
            ))}
          </Row>
        </div>
      )}

      <Row className="g-3">
        {results.map((item) => {
          const active = item.sources[item.activeSource];
          const coverKey = active ? `${item.activeSource}-${active.pic_id}-300` : null;
          const cover = (coverKey && coverCache[coverKey]) || 'default_cover.png';
          const isCurrent = currentTrack?.id === active?.id && currentTrack?.source === active?.source;
          return (
            <Col key={item.key} xs={12} sm={6} lg={4}>
              <div className="result-card">
                <img src={cover} alt="专辑封面" className="result-cover"
                  onError={(e) => { e.target.src = 'default_cover.png'; }} />
                <div className="result-info">
                  <div className="result-name" title={item.name}>{item.name}</div>
                  <div className="result-artist">{item.artist}</div>
                  {/* 可用音源：圆点 radio，点击换源 */}
                  <div className="source-badges">
                    {WORKING_SOURCES.filter(s => item.sources[s.key]).map(s => (
                      <button key={s.key} type="button"
                        className={`src-badge ${item.activeSource === s.key ? 'active' : ''}`}
                        title={`切换到${s.name}`}
                        onClick={() => switchSource(item, s.key)}>
                        <span className="src-dot" style={{ background: item.activeSource === s.key ? s.color : 'transparent', borderColor: s.color }}></span>
                        <span className="src-label">{s.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="result-actions">
                  <button type="button" className="act-btn play" title="播放" onClick={() => handlePlay(item)}
                    disabled={loading && isCurrent}>
                    {loading && isCurrent ? <Spinner animation="border" size="sm" />
                      : isCurrent && isPlaying ? <FaPause /> : <FaPlay />}
                  </button>
                  <button type="button" className="act-btn add" title="添加到播放列表" onClick={() => addToPlaylist(item)}>
                    <FaPlus />
                  </button>
                  <button type="button" className="act-btn dl" title="下载" onClick={() => handleDownload(item)}>
                    <FaDownload />
                  </button>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>

      {showPlayer && (
        <div className="fixed-bottom bg-light p-3 border-top" style={{ height: lyricExpanded ? '300px' : 'auto', boxShadow: '0 -2px 10px rgba(0,0,0,0.1)', zIndex: 1000 }}>
          <button type="button" className="player-close" onClick={closePlayer} title="关闭试听" aria-label="关闭试听">×</button>
          <div className="player-inner">
        <Row className="align-items-center">
          <Col md={3}>
            <div className="d-flex ">
              {currentTrack && (
                <div className="d-flex align-items-center">
                  <img
                    src={coverCache[`${currentTrack.source}-${currentTrack.pic_id}-300`] || 'default_cover.png'}
                    alt="当前播放" style={{ width: '50px', height: '50px' }} className="me-2 rounded"
                  />
                  <div>
                    <h6 className="mb-0">{currentTrack.name}</h6>
                    <small className="text-muted">{currentTrack.artist}</small>
                  </div>
                </div>
              )}
              <Button variant="link" onClick={() => setLyricExpanded(!lyricExpanded)} className="ms-2"
                title={lyricExpanded ? '收起歌词' : '展开歌词'}>
                {lyricExpanded ? <FaChevronDown /> : <FaChevronUp />}
              </Button>
            </div>
          </Col>

          <Col md={6}>
            <div className={`lyric-container ${lyricExpanded ? 'expanded' : 'collapsed'}`}
              style={{ maxHeight: lyricExpanded ? '400px' : '60px', transition: 'max-height 0.3s ease' }}>
              <div className="lyric-wrapper">
                {lyricData.parsedLyric[currentLyricIndex] && (
                  <div className="current-lyric">
                    {lyricData.parsedLyric[currentLyricIndex].text}
                    {lyricData.tLyric && (
                      <div className="translated-lyric">
                        {parseLyric(lyricData.tLyric)[currentLyricIndex]?.text}
                      </div>
                    )}
                  </div>
                )}
                {lyricExpanded && (
                  <div className="full-lyrics" ref={lyricsContainerRef}
                    onScroll={() => { sessionStorage.setItem('userScrolled', true); }}>
                    {lyricData.parsedLyric.map((line, index) => (
                      <div key={index} className={`lyric-line ${index === currentLyricIndex ? 'active' : ''}`} data-time={line.time}>
                        <div>{line.text}</div>
                        {lyricData.tLyric && (
                          <div className="translated-lyric">{parseLyric(lyricData.tLyric)[index]?.text}</div>
                        )}
                      </div>
                    ))}
                    {lyricData.parsedLyric.length === 0 && (
                      <div className="text-center text-muted py-3">暂无歌词</div>
                    )}
                  </div>
                )}
                {lyricData.parsedLyric.length === 0 && (
                  <div className="current-lyric">暂无歌词</div>
                )}
              </div>
            </div>
            <ReactPlayer
              ref={playerRef}
              onProgress={handleProgress}
              url={playerUrl}
              playing={isPlaying}
              onReady={() => console.log('播放器就绪')}
              onError={(e) => { console.error('播放错误:', e); setIsPlaying(false); }}
              onEnded={() => { setIsPlaying(false); }}
              config={{ file: { forceAudio: true } }}
              height={0}
              style={{ display: playerUrl ? 'block' : 'none' }}
            />
          </Col>

          <Col md={3} className="text-end">
            <Button variant="link" onClick={() => setIsPlaying(!isPlaying)} disabled={!currentTrack || !playerUrl}>
              {!currentTrack ? <FaMusic size={24} className="text-muted" />
                : isPlaying ? <FaPause size={24} /> : <FaPlay size={24} />}
            </Button>
          </Col>
        </Row>
          </div>
        </div>
      )}

      <footer className="site-footer">
        <div className="footer-title">关于本站</div>
        <p>免费音乐搜索是一款免费的音乐聚合工具，聚合 网易云音乐、酷狗音乐、咪咕音乐 多平台曲库，支持免费在线听歌、MP3下载、音乐下载、歌词展示与高品质试听。</p>
        <div className="footer-title">免责声明</div>
        <p>本站所有音频数据均来自第三方接口，本站不存储任何音频文件。音乐版权归原作者及平台所有，仅供学习交流使用，请于下载后 24 小时内删除。</p>
        <div className="footer-title">友情链接</div>
        <div className="footer-links">
          <a href="http://www.jbsou.cn" target="_blank" rel="noopener noreferrer">煎饼音乐网</a>
          <a href="http://www.52ape.com" target="_blank" rel="noopener noreferrer">51ape音乐</a>
          <a href="http://www.at38.cn" target="_blank" rel="noopener noreferrer">51音乐下载</a>
          <a href="http://www.musicso.cc" target="_blank" rel="noopener noreferrer">音乐搜</a>
        </div>
        <div className="footer-copy">© 2026 CL-Music · 仅供学习交流 · Powered by React</div>
      </footer>

      {downloadModal.show && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(0,0,0,0.55)', zIndex: 9999 }}>
          <div className="bg-white rounded-4 p-4 shadow-lg" style={{ minWidth: '340px', maxWidth: '90%' }}>
            <div className="text-center mb-3">
              <div className="mb-2"><FaDownload size={32} className="text-success" /></div>
              <h5 className="mb-1 fw-bold">正在下载</h5>
              <div className="text-muted small text-truncate px-2">
                {downloadModal.track?.name} - {downloadModal.track?.artist}
              </div>
            </div>
            <ProgressBar now={downloadModal.progress} label={`${downloadModal.progress}%`}
              className="mb-2" variant="success" style={{ height: '10px' }} />
            <div className="text-center text-muted small fw-semibold">{downloadModal.progress}%</div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default MusicSearch;
